import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useMeta, eventJsonLd } from "@/lib/seo";
import SearchBar from "@/components/SearchBar";
import QuickFilters from "@/components/QuickFilters";
import FilterPanel from "@/components/FilterPanel";
import ActiveFilters, { activeFilterList } from "@/components/ActiveFilters";
import EventListView from "@/components/EventListView";
import FeaturedRecommends from "@/components/FeaturedRecommends";
import EventCompactList from "@/components/EventCompactList";
import EventCalendarView from "@/components/EventCalendarView";
import EventMapView from "@/components/EventMapView";
import { List, Calendar, Map as MapIcon, LayoutGrid, AlignJustify } from "lucide-react";
import { toast } from "sonner";
import { PAGE_SIZE, toParams } from "@/lib/constants";

export default function Home({ forcedView }) {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const [filters, setFilters] = useState({});
  const [events, setEvents] = useState([]);
  const [view, setView] = useState(() => forcedView || localStorage.getItem("gg_view") || "list");
  const [listMode, setListMode] = useState(() => localStorage.getItem("gg_list_mode") || "cards");
  const changeListMode = m => { localStorage.setItem("gg_list_mode", m); setListMode(m); };
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [locating, setLocating] = useState(false);
  const sentinel = useRef(null);
  const reqId = useRef(0);

  useEffect(() => { if (forcedView) setView(forcedView); }, [forcedView]);

  const resolve = async (f) => {
    const p = toParams(f);
    if (f.radius_km && f.city && !f.nearMe) {
      try { const { data } = await api.get("/geocode", { params: { q: f.city } }); p.lat = data.lat; p.lng = data.lng; p.radius_km = f.radius_km; delete p.city; }
      catch { toast.error(t("locationNotFound")); }
    }
    return p;
  };

  const load = useCallback(async (f, append = false, skip = 0) => {
    const id = ++reqId.current;
    append ? setMore(true) : setLoading(true);
    try {
      const params = await resolve(f);
      const isMapOrCal = view !== "list";
      const { data } = await api.get("/events", { params: { ...params, limit: isMapOrCal ? 500 : PAGE_SIZE, skip } });
      if (id !== reqId.current) return;
      setEvents(prev => append ? [...prev, ...data.items] : data.items);
      setHasMore(!isMapOrCal && data.items.length === PAGE_SIZE);
    } finally { if (id === reqId.current) { setLoading(false); setMore(false); } }
  }, [view]); // eslint-disable-line

  useEffect(() => { load(filters); }, [view]); // eslint-disable-line

  useEffect(() => {
    if (!sentinel.current || view !== "list") return;
    const io = new IntersectionObserver(([en]) => { if (en.isIntersecting && hasMore && !loading && !more) load(filters, true, events.length); }, { rootMargin: "600px" });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [events, hasMore, loading, more, view, filters, load]);

  const apply = (next) => { setFilters(next); load(next); };

  const locate = () => {
    if (filters.nearMe) { apply({ ...filters, nearMe: false, lat: undefined, lng: undefined, radius_km: "" }); return; }
    if (!navigator.geolocation) { toast.error(t("locationDenied")); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setLocating(false); apply({ ...filters, nearMe: true, city: "", lat: pos.coords.latitude, lng: pos.coords.longitude, radius_km: filters.radius_km || 50 }); },
      () => { setLocating(false); toast.error(t("locationDenied")); },
      { timeout: 8000 },
    );
  };

  const changeView = (v) => {
    localStorage.setItem("gg_view", v);
    if (forcedView) { nav(v === "map" ? "/map" : "/"); if (v !== "map") localStorage.setItem("gg_view", v); return; }
    setView(v);
  };
  const activeCount = activeFilterList(filters, t, lang).length;
  const { user } = useApp();
  useMeta({ title: forcedView === "map" ? t("seoMapTitle") : null, description: t("seoHomeDesc"), path: forcedView === "map" ? "/map" : "/" });
  const [nextEv, setNextEv] = useState(null);
  useEffect(() => { if (user) api.get("/my/overview").then(r => setNextEv(r.data.next_event)).catch(() => setNextEv(null)); else setNextEv(null); }, [user]);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-10 pb-16">
      {nextEv && (
        <Link to={`/events/${nextEv.slug}`} className="flex justify-end items-center gap-1.5 min-w-0 mb-8 md:mb-10 -mt-1 md:-mt-3 text-xs hover:text-primary transition-colors" data-testid="home-next-event">
          <span className="eyebrow !text-[10px] text-primary shrink-0">{t("yourNextEvent")}:</span>
          <span className="font-display uppercase tracking-wide font-semibold truncate">{nextEv.name}</span>
          <span className="text-muted-foreground shrink-0">· {nextEv.city}</span>
          <span className="text-primary shrink-0">→</span>
        </Link>
      )}
      <section className="text-center" data-testid="hero">
        <h1 className="flex justify-center items-center h-[58px] sm:h-[70px] lg:h-[82px]" data-testid="hero-brand">
          <img src="/brand/soul-radar-full.png" alt="Soul Radar – Your Soul Event Finder" className="h-[54px] sm:h-[66px] lg:h-[80px] w-auto max-w-full dark:[filter:invert(1)_hue-rotate(180deg)]" data-testid="hero-logo" />
        </h1>
        <h2 className="mt-5 md:mt-7 font-display uppercase text-xl md:text-2xl tracking-tight" data-testid="hero-prompt">{t("heroPrompt")}</h2>
        <div className="mt-3 md:mt-4 max-w-4xl mx-auto">
          <SearchBar value={filters} onChange={setFilters} onSubmit={() => apply(filters)} onLocate={locate} locating={locating} />
        </div>
        <div className="mt-3 max-w-4xl mx-auto"><QuickFilters value={filters} onApply={(patch) => apply({ ...filters, ...patch })} onLocate={locate} /></div>
      </section>

      <section className="mt-8 md:mt-12">
        <div className="flex items-center justify-between gap-3 border-b border-foreground pb-3 mb-4 flex-wrap">
          <div className="flex border border-foreground" data-testid="view-toggle">
            {[["list", List, t("viewList")], ["calendar", Calendar, t("viewCalendar")], ["map", MapIcon, t("viewMap")]].map(([k, Icon, label]) => (
              <button key={k} data-testid={`view-${k}`} onClick={() => changeView(k)}
                className={`flex items-center gap-2 px-3 min-h-[40px] font-display uppercase text-xs tracking-wider border-r border-foreground last:border-r-0 transition-colors duration-200 ${view === k ? "bg-foreground text-background" : "hover:bg-muted"}`}>
                <Icon className="w-4 h-4" /><span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
          <FilterPanel value={filters} onApply={apply} activeCount={activeCount} />
        </div>
        {view === "list" && <FeaturedRecommends />}
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
          <h2 className="font-display uppercase text-lg md:text-xl tracking-tight text-muted-foreground" data-testid="feed-title">{activeCount ? (events.length === 1 ? t("oneEventFound") : t("eventsFound", { n: events.length })) : t("upcomingEvents")}</h2>
          {view === "list" && (
            <div className="flex items-center gap-0.5 ml-auto order-last sm:order-none" role="group" aria-label={t("listMode")} data-testid="list-mode-toggle">
              {[["cards", LayoutGrid, t("viewCards")], ["compact", AlignJustify, t("viewCompact")]].map(([k, Icon, label]) => (
                <button key={k} type="button" onClick={() => changeListMode(k)} title={label} aria-pressed={listMode === k} data-testid={`list-mode-${k}`}
                  className={`inline-flex items-center gap-1.5 px-2 min-h-[32px] font-display uppercase text-[10px] tracking-wider transition-colors ${listMode === k ? "text-foreground border-b-2 border-primary" : "text-muted-foreground hover:text-foreground border-b-2 border-transparent"}`}>
                  <Icon className="w-3.5 h-3.5" /><span>{label}</span>
                </button>
              ))}
            </div>
          )}
          {activeCount > 0 && !loading && <span className="text-xs text-muted-foreground" data-testid="result-context">{[
            filters.nearMe ? `${t("nearYou")} · ${filters.radius_km || 50} km` : filters.radius_km && filters.city ? t("withinKmOf", { km: filters.radius_km, place: filters.city }) : filters.city,
            filters.month ? `${t("monthHeaders")[+filters.month.split("-")[1] - 1]} ${filters.month.split("-")[0]}` : filters.year || null,
            filters.genre ? filters.genre.split(",").filter(Boolean).map(g => t(`genres.${g}`)).join(", ") : null, filters.event_type ? t(`types.${filters.event_type}`) : null,
          ].filter(Boolean).join(" · ")}</span>}
        </div>
        <div className="mb-4"><ActiveFilters value={filters} onChange={apply} /></div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="gg-card animate-pulse grid md:grid-cols-[200px_1fr]">
                <div className="aspect-[4/3] md:aspect-auto md:h-36 bg-muted" />
                <div className="p-4 space-y-2"><div className="h-3 bg-muted w-1/4" /><div className="h-6 bg-muted w-2/3" /><div className="h-3 bg-muted w-1/3" /></div>
              </div>
            ))}
          </div>
        ) : view === "list" ? (
          <>
            {listMode === "compact" ? <EventCompactList events={events} /> : <EventListView events={events} />}
            <div ref={sentinel} className="h-1" />
            {more && <div className="text-center eyebrow py-6" data-testid="loading-more">{t("loadingMore")}</div>}
            {!hasMore && events.length > 0 && <div className="text-center eyebrow py-8 text-muted-foreground" data-testid="end-of-list">{t("endOfList")}</div>}
          </>
        ) : view === "calendar" ? <EventCalendarView events={events} />
          : <EventMapView events={events} />}
      </section>
    </div>
  );
}
